import type { LayoutLine, ProcessedWord } from "./SplitMeasuredTokens.ts";
import type { ISplitOptions } from "../../models/text/ISplitOptions.ts";
import type { CalculatedValue } from "../../models/text/CalculatedValue.ts";

export type SplitAlignOption = {
  align: string;
  random?: { min: number; max: number };
};

/**
 * Computes a per-item numeric value (delay/offset/etc.) for a "random",
 * "start", "end", or "center" alignment strategy used by `string-split`
 * options like `[center]` or `[random(0,10)]`.
 */
export class SplitAlignValueCalculator {
  static compute(opt: SplitAlignOption, index: number, total: number): number {
    if (opt.align.startsWith("random")) {
      const min = opt.random?.min ?? 0;
      const max = opt.random?.max ?? total - 1;
      return Math.floor(Math.random() * (max - min + 1)) + min;
    }

    switch (opt.align) {
      case "start":
        return index;
      case "end":
        return total - index - 1;
      case "center": {
        const centerIdx = Math.floor((total - 1) / 2);
        return Math.abs(index - centerIdx);
      }
      default:
        return index;
    }
  }

  /**
   * Applies `compute()` across every `line`/`word`/`wordLine`/`char`/
   * `charWord`/`charLine` option in `options`, writing the results onto each
   * line/word/char's `calculatedValues`. Mutates `lines` in place.
   */
  static applyToLines(lines: LayoutLine[], options: ISplitOptions): void {
    const countChars = (ln: LayoutLine) =>
      ln.words.reduce((acc, w: ProcessedWord) => acc + w.chars.length, 0);

    const totalWords = lines.reduce((acc, l) => acc + l.words.length, 0);
    const totalCharsGlobal = lines.reduce(
      (acc, l) => acc + l.words.reduce((acc2, w) => acc2 + w.chars.length, 0),
      0,
    );

    lines.forEach((line, li) => {
      if (options.line) {
        line.calculatedValues = options.line.map(
          (opt) =>
            ({
              type: "line",
              align: opt.align,
              value: SplitAlignValueCalculator.compute(opt, li, lines.length),
            }) as CalculatedValue,
        );
      }

      line.words.forEach((word: ProcessedWord) => {
        if (options.word) {
          word.calculatedValues = options.word.map(
            (opt) =>
              ({
                type: "word",
                align: opt.align,
                value: SplitAlignValueCalculator.compute(opt, word.wordIndexGlobal, totalWords),
              }) as CalculatedValue,
          );
        }

        if (options.wordLine) {
          word.calculatedValues ??= [];
          word.calculatedValues.push(
            ...options.wordLine.map(
              (opt) =>
                ({
                  type: "wordLine",
                  align: opt.align,
                  value: SplitAlignValueCalculator.compute(opt, word.wordIndexInLine, line.words.length),
                }) as CalculatedValue,
            ),
          );
        }

        const totalCharsInLine = countChars(line);

        word.chars.forEach((char) => {
          const cvs: CalculatedValue[] = [];

          if (options.char) {
            cvs.push(
              ...options.char.map(
                (opt) =>
                  ({
                    type: "char",
                    align: opt.align,
                    value: SplitAlignValueCalculator.compute(opt, char.charIndexGlobal, totalCharsGlobal),
                  }) as CalculatedValue,
              ),
            );
          }

          if (options.charWord) {
            cvs.push(
              ...options.charWord.map(
                (opt) =>
                  ({
                    type: "charWord",
                    align: opt.align,
                    value: SplitAlignValueCalculator.compute(opt, char.charIndexInWord, word.chars.length),
                  }) as CalculatedValue,
              ),
            );
          }

          if (options.charLine) {
            cvs.push(
              ...options.charLine.map(
                (opt) =>
                  ({
                    type: "charLine",
                    align: opt.align,
                    value: SplitAlignValueCalculator.compute(opt, char.charIndexInLine, totalCharsInLine),
                  }) as CalculatedValue,
              ),
            );
          }

          char.calculatedValues = cvs;
        });
      });
    });
  }
}
